/**
 * useScheduleDraftEditPipeline — ScheduleDraft 编辑命令执行管道 hook。
 *
 * 本 hook 固定 validate → recalculate（含 ChangeSet 构建）→ confirmWarnings →
 * applyToLocal → pushUndoStack → markDirty 的本地编辑执行序列，供 DataGrid、
 * 弹窗和未来 Agent 操作共享同一编辑生命周期。
 *
 * 核心设计决策：
 * - Pipeline 不再发起 HTTP 请求——mutation 职责迁移到独立的 saveAllChanges 流程
 * - ChangeSet 由 Pipeline 层统一构建——重算规则函数只负责返回 { patches, warnings }
 * - before/after 来源一致——from localFlightsMap 的完整航班快照
 *
 * 设计文档 → docs/30_editing_publish/30_local_edit_and_undo.md §5
 * 骨架关联 → lib/editCommand.js (命令模型), lib/editCommandPipeline.js (ChangeSet 构建), lib/validation/index.js (校验归一化),
 *             hooks/useLocalEditState.js (本地状态 API),
 *             context/ScheduleDraftEditorWorkspaceContext.jsx (执行入口)
 */

import { useCallback } from "react";
import { message } from "antd";
import { isEditCommand } from "../lib/editCommand";
import { changeSetAffectsAnalysis } from "../lib/fieldImpactUtils";
import { hasBlockingIssues, normalizeValidationIssues, splitValidationIssues } from "../lib/validation";

function showPipelineWarnings(issues = []) {
    const { warningIssues } = splitValidationIssues(issues);
    const warningMessages = warningIssues
        .map((issue) => issue?.message)
        .filter(Boolean)
        .map((warning) => String(warning));
    if (!warningMessages.length) {
        return;
    }

    const uniqueWarnings = Array.from(new Set(warningMessages));
    const summary = uniqueWarnings.slice(0, 3).join("；");
    const suffix = uniqueWarnings.length > 3 ? "；..." : "";
    const prefix = warningMessages.length > 1 ? `本次操作产生 ${warningMessages.length} 条提示：` : "";
    message.warning(`${prefix}${summary}${suffix}`);
}

/**
 * @typedef {import("../lib/editCommand").EditCommand} EditCommand
 * @typedef {import("../lib/editCommand").EditPipelineResult} EditPipelineResult
 * @typedef {import("../lib/editCommand").ValidationIssue} ValidationIssue
 */

/**
 * @typedef {Object} ChangeSetChange
 * @property {string} flightId Affected flight ID.
 * @property {string} field Affected field name.
 * @property {*} before Value before the change.
 * @property {*} after Value after the change.
 */

/**
 * @typedef {Object} ChangeSet
 * @property {string} id Unique ID for debugging.
 * @property {string} type Operation type, mirrors EditCommand type.
 * @property {string} label Human-readable description.
 * @property {number} timestamp Operation timestamp.
 * @property {ChangeSetChange[]} changes All field-level changes.
 */

/**
 * @typedef {Object} RecalculateCommandResult
 * @property {EditCommand} command The (possibly enriched) command.
 * @property {ChangeSet} changeSet The constructed ChangeSet with before/after values.
 * @property {ValidationIssue[]} warnings Recalculation warnings (non-blocking).
 */

/**
 * @typedef {Object} ScheduleDraftEditPipelineOptions
 * @property {(command: EditCommand) => (ValidationIssue[]|Promise<ValidationIssue[]>)} [validateCommand]
 *   Client-side validation before recalculation. Return blocking issues to abort.
 * @property {(command: EditCommand) => (RecalculateCommandResult|Promise<RecalculateCommandResult>)} [recalculateCommand]
 *   Recalculation function that produces a ChangeSet with before/after values.
 *   Must receive the complete local flight from localFlightsMap as context.
 * @property {(issues: ValidationIssue[], command: EditCommand) => (boolean|Promise<boolean>)} [confirmWarnings]
 *   Optional warning confirmation callback. Return false to cancel.
 * @property {(changeSet: ChangeSet) => void} applyChangeSet
 *   Writes ChangeSet.after values to localState and pushes to undo stack.
 * @property {Function} [refetchAnalysis]
 *   Refreshes backend-owned analysis after a local ChangeSet is applied.
 */

/**
 * Builds the local-edit pipeline for ScheduleDraft.
 *
 * Pipeline sequence (local edit model):
 *   executeCommand(command)
 *     → validateBeforeCommit(command)
 *     → buildChangeSet(command)          // recalculate + construct ChangeSet
 *     → maybeConfirmWarnings(allIssues)
 *     → applyChangeSet(changeSet)        // write to localState + push undo
 *
 * @param {ScheduleDraftEditPipelineOptions} options Pipeline extension points.
 * @returns {{
 *   executeCommand: (command: EditCommand) => Promise<EditPipelineResult>,
 *   validateBeforeCommit: (command: EditCommand) => Promise<ValidationIssue[]>,
 *   buildChangeSet: (command: EditCommand) => Promise<RecalculateCommandResult>,
 *   maybeConfirmWarnings: (issues: ValidationIssue[], command: EditCommand) => Promise<boolean>,
 * }} Pipeline operations for editor integration.
 */
export function useScheduleDraftEditPipeline({
    validateCommand,
    recalculateCommand,
    confirmWarnings,
    applyChangeSet,
    refetchAnalysis,
} = {}) {
    /**
     * Runs client-side validation before any recalculation is attempted.
     *
     * @param {EditCommand} command Command to validate.
     * @returns {Promise<ValidationIssue[]>} Normalized client validation issues.
     */
    const validateBeforeCommit = useCallback(async (command) => {
        const rawIssues = validateCommand ? await validateCommand(command) : [];
        return normalizeValidationIssues(rawIssues, { source: "client" });
    }, [validateCommand]);

    /**
     * Runs recalculation and constructs a ChangeSet with before/after values.
     *
     * The recalculateCommand callback is responsible for:
     * 1. Reading the complete current flight from localFlightsMap (before snapshot)
     * 2. Calling editCommandPipeline/field derivation to get patches + warnings
     * 3. Merging userEdit + patches into after values
     * 4. Returning { command, changeSet, warnings }
     *
     * @param {EditCommand} command Command to recalculate.
     * @returns {Promise<RecalculateCommandResult>} Recalculated result with ChangeSet.
     */
    const buildChangeSet = useCallback(async (command) => {
        if (!recalculateCommand) {
            // Fallback: no recalculation configured, build a minimal ChangeSet from payload
            const changes = [];
            const { flightId, payload } = command;
            if (flightId && payload) {
                Object.entries(payload).forEach(([field, value]) => {
                    changes.push({
                        flightId: String(flightId),
                        field,
                        before: undefined, // Cannot determine without localFlightsMap access
                        after: value,
                    });
                });
            }

            return {
                command,
                changeSet: {
                    id: `cs-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
                    type: command.type,
                    label: `${command.type}`,
                    timestamp: Date.now(),
                    changes,
                },
                warnings: [],
            };
        }

        return recalculateCommand(command);
    }, [recalculateCommand]);

    /**
     * Gives the UI a chance to confirm non-blocking warnings.
     *
     * This step runs AFTER recalculation so that recalculation-produced warnings
     * (e.g., "机场变更导致已绑定航路被清除") are included in the confirmation.
     *
     * @param {ValidationIssue[]} issues All issues (client validation + recalculation warnings).
     * @param {EditCommand} command Command being executed.
     * @returns {Promise<boolean>} True when execution may continue.
     */
    const maybeConfirmWarnings = useCallback(async (issues, command) => {
        const { warningIssues } = splitValidationIssues(issues);
        if (warningIssues.length === 0 || !confirmWarnings) {
            return true;
        }

        return Boolean(await confirmWarnings(warningIssues, command));
    }, [confirmWarnings]);

    /**
     * Executes one edit command through the local-edit pipeline:
     * validate → buildChangeSet → confirmWarnings → applyChangeSet.
     *
     * No HTTP requests are made. The resulting ChangeSet is applied to localState
     * and pushed to the undo stack via the applyChangeSet callback.
     *
     * @param {EditCommand} command Command submitted by an editor surface.
     * @returns {Promise<EditPipelineResult>} Pipeline result with status and changeSet.
     */
    const executeCommand = useCallback(async (command) => {
        if (!isEditCommand(command)) {
            throw new Error("Invalid schedule draft edit command.");
        }

        // Step 1: Client-side validation
        const clientIssues = await validateBeforeCommit(command);
        if (hasBlockingIssues(clientIssues)) {
            return {
                status: "blocked",
                command,
                issues: clientIssues,
            };
        }

        // Step 2: Recalculate + build ChangeSet (before/after from localFlightsMap)
        const { command: enrichedCommand, changeSet, warnings } = await buildChangeSet(command);

        // Merge validation issues: client issues + recalculation warnings
        const recalcWarningIssues = (warnings || []).map((msg, index) =>
            typeof msg === "string"
                ? {
                      scope: "flight",
                      entityId: command.flightId,
                      severity: "warning",
                      code: `RECALC_WARNING_${index}`,
                      message: msg,
                      blocking: false,
                      source: "recalculation",
                  }
                : msg,
        );
        const allIssues = [...clientIssues, ...normalizeValidationIssues(recalcWarningIssues, { source: "recalculation" })];

        // Step 3: Confirm warnings (after recalculation, per design doc §17 review 2.4)
        const confirmed = await maybeConfirmWarnings(allIssues, enrichedCommand || command);
        if (!confirmed) {
            return {
                status: "canceled",
                command,
                changeSet,
                issues: allIssues,
            };
        }

        // Step 4+5+6: Apply to localState + push undo stack + mark dirty
        // All three steps are handled atomically by useLocalEditState.applyChangeSet
        if (!applyChangeSet) {
            throw new Error("useScheduleDraftEditPipeline requires an applyChangeSet callback.");
        }

        if (changeSet?.changes?.length) {
            applyChangeSet(changeSet);
            if (changeSetAffectsAnalysis(changeSet)) {
                // The Context-level analysis query sends current-track working_flights.
                refetchAnalysis?.();
            }
        }

        const result = {
            status: "success",
            command,
            changeSet,
            issues: allIssues,
        };

        if (!confirmWarnings && result.issues?.length) {
            showPipelineWarnings(result.issues);
        }

        return result;
    }, [
        applyChangeSet,
        buildChangeSet,
        confirmWarnings,
        maybeConfirmWarnings,
        refetchAnalysis,
        validateBeforeCommit,
    ]);

    return {
        executeCommand,
        validateBeforeCommit,
        buildChangeSet,
        maybeConfirmWarnings,
    };
}

export default useScheduleDraftEditPipeline;
